import React, { useState, useEffect } from "react";
import FeedBackService from "../../../Api/FeedBackService";
import CourseService from "../../../Api/CourseService";

const Reviews = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [purchasedCourses, setPurchasedCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const feedbackService = new FeedBackService();
    const courseService = new CourseService();

    const [showModal, setShowModal] = useState(false);
    const [currentFeedback, setCurrentFeedback] = useState(null);

    useEffect(() => {
        fetchUserFeedbacks();
        fetchPurchasedCourses();
    }, []);

    const fetchUserFeedbacks = async (page = 1, limit = 10) => {
        try {
            const response = await feedbackService.GetUserSubmittedFeedBacks(page, limit);
            if (response.data.status === "success") {
                setFeedbacks(response.data.feedbacks);
            } else {
                setError("Failed to load feedbacks");
            }
        } catch (err) {
            setError("Error loading feedbacks");
            console.error("Error fetching feedbacks:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchPurchasedCourses = async () => {
        try {
            const response = await courseService.GetPurchaseCourse();
            if (response.data.status === "success") {
                setPurchasedCourses(response.data.data);
            } else {
                console.error("Failed to load purchased courses");
            }
        } catch (err) {
            console.error("Error fetching purchased courses:", err);
        }
    };

    const handleAddFeedbackClick = () => {
        setCurrentFeedback(null); // Clear any previous feedback data
        setShowModal(true);
    };

    const handleEditFeedbackClick = (feedback) => {
        setCurrentFeedback(feedback);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentFeedback(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCurrentFeedback({
            ...currentFeedback,
            [name]: value
        });
    };

    const handleRatingChange = (rating) => {
        setCurrentFeedback({
            ...currentFeedback,
            rating: rating
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Explicitly check if a course is selected
        if (!currentFeedback || !currentFeedback.course_id) {
            alert("Please select a course.");
            return;
        }

        if (!currentFeedback.rating) {
             alert("Please provide a rating.");
             return;
        }

        if (!currentFeedback.comment) {
             alert("Please provide a comment.");
             return;
        }

        try {
            setLoading(true);
            if (currentFeedback.id) {
                // Update existing feedback
                await feedbackService.UpdateFeedBack(currentFeedback.id, {
                    rating: parseInt(currentFeedback.rating),
                    comment: currentFeedback.comment
                });
            } else {
                // Create new feedback
                const createResponse = await feedbackService.CreateFeedBack({
                    course_id: parseInt(currentFeedback.course_id),
                    rating: parseInt(currentFeedback.rating),
                    comment: currentFeedback.comment
                });
                
                if (createResponse.data.status === "error" && createResponse.data.message === "You have already submitted feedback for this course") {
                    alert(createResponse.data.message);
                    setLoading(false); // Stop loading
                    return; // Stop further processing
                }
            }
            setShowModal(false);
            setCurrentFeedback(null);
            fetchUserFeedbacks(); // Refresh the list
        } catch (err) {
            // Check for specific 409 conflict error
            if (err.response && err.response.status === 409 && err.response.data && err.response.data.status === "error" && err.response.data.message === "You have already submitted feedback for this course") {
                alert(err.response.data.message);
            } else {
                // Handle other errors
                setError("Error saving feedback");
                console.error("Error saving feedback:", err);
            }
            setLoading(false);
        }
    };

    const handleDeleteFeedback = async (id) => {
        console.log("Attempting to delete feedback with ID:", id);
        try {
            await feedbackService.DeleteFeedBackById(id);
            console.log("Feedback deleted successfully.");
            fetchUserFeedbacks(); // Refresh the list
        } catch (err) {
            setError("Error deleting feedback");
            console.error("Error deleting feedback:", err);
        }
    };

    if (loading) {
        return (
            <div className="content">
                <div className="section-title">
                    <h4 className="rbt-title-style-3">Reviews</h4>
                </div>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="content">
                <div className="section-title">
                    <h4 className="rbt-title-style-3">Reviews</h4>
                </div>
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="content">
                <div className="section-title">
                    <h4 className="rbt-title-style-3">Reviews</h4>
                </div>

                <div className="d-flex justify-content-center mb-4">
                    <button className="rbt-btn btn-gradient btn-sm px-4" onClick={handleAddFeedbackClick}>Add New Feedback</button>
                </div>

                <div className="row justify-content-center">
                    <div className="col-lg-10 col-md-12">
                        <div className="rbt-dashboard-table table-responsive mobile-table-750">
                            <table className="rbt-table table table-borderless align-middle text-center">
                                <thead>
                                    <tr>
                                        <th className="text-center">Course</th>
                                        <th className="text-center">Feedback</th>
                                        <th className="text-center" style={{ width: '120px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {feedbacks.length > 0 ? (
                                        feedbacks.map((feedback) => (
                                            <tr key={feedback.id}>
                                                <th className="align-middle">
                                                    <span className="b3">
                                                        <a href="#">{feedback.Course.title}</a>
                                                    </span>
                                                </th>
                                                <td className="align-middle">
                                                    <div className="rbt-review">
                                                        <div className="rating">
                                                            {[...Array(5)].map((_, index) => (
                                                                <i 
                                                                    key={index} 
                                                                    className={`fas fa-star ${index < feedback.rating ? 'text-warning' : ''}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <p className="b2">{feedback.comment}</p>
                                                </td>
                                                <td className="align-middle">
                                                    <div className="d-flex justify-content-center align-items-center gap-2">
                                                        <a
                                                            className="rbt-btn btn-xs bg-primary-opacity radius-round"
                                                            href="#"
                                                            title="Edit"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleEditFeedbackClick(feedback);
                                                            }}
                                                        >
                                                            <i className="feather-edit pl--0" />
                                                        </a>
                                                        <a
                                                            className="rbt-btn btn-xs bg-color-danger-opacity radius-round color-danger"
                                                            href="#"
                                                            title="Delete"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (window.confirm('Are you sure you want to delete this feedback?')) {
                                                                    handleDeleteFeedback(feedback.id);
                                                                }
                                                            }}
                                                        >
                                                            <i className="feather-trash-2 pl--0" />
                                                        </a>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3" className="text-center">No feedbacks submitted yet.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Modal */}
            {showModal && (
               <div className="modal show d-block fade" tabIndex="-1" role="dialog">
  <div className="modal-dialog modal-dialog-centered" role="document">
    <div className="modal-content shadow rounded-4">
      <div className="modal-header border-0">
        <h5 className="modal-title fw-bold">
          {currentFeedback?.id ? 'Edit Feedback' : 'Add New Feedback'}
        </h5>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          onClick={handleCloseModal}
        ></button>
      </div>
      <div className="modal-body px-4">
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="course_id" className="form-label fw-semibold">
              Course
            </label>
            <select
              className="form-select"
              id="course_id"
              name="course_id"
              value={currentFeedback?.course_id || ''}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>Select a Course</option>
              {purchasedCourses.map(course => (
                <option key={course.course.id} value={course.course.id}>
                  {course.course.title}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label htmlFor="rating" className="form-label fw-semibold">
              Rating
            </label>
            <div className="d-flex gap-1">
              {[...Array(5)].map((_, index) => (
                <i
                  key={index}
                  className={`fas fa-star fs-4 ${index < (currentFeedback?.rating || 0) ? 'text-warning' : 'text-secondary'}`}
                  onClick={() => handleRatingChange(index + 1)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => handleRatingChange(index + 1)}
                />
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="comment" className="form-label fw-semibold">
              Comment
            </label>
            <textarea
              className="form-control"
              id="comment"
              name="comment"
              rows="4"
              placeholder="Write your feedback..."
              value={currentFeedback?.comment || ''}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="modal-footer border-0 pt-0">
            <button
              type="button"
              className="btn btn-light border"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {currentFeedback?.id ? 'Update Feedback' : 'Create Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</div>

            )}

        </>
    );
};

export default Reviews;
